import {
  FEATURE_ACCESS_POLICIES,
  SUBSCRIPTION_STATUS,
  SUBSCRIPTION_TIER,
  TIER_ENTITLEMENTS,
} from '../config/entitlements';

export function normalizeTier(rawTier) {
  if (!rawTier) return SUBSCRIPTION_TIER.NONE;

  const normalizedTier = String(rawTier).toLowerCase();
  return Object.values(SUBSCRIPTION_TIER).includes(normalizedTier)
    ? normalizedTier
    : SUBSCRIPTION_TIER.NONE;
}

export function normalizeStatus(rawStatus) {
  if (!rawStatus) return SUBSCRIPTION_STATUS.NONE;

  const normalizedStatus = String(rawStatus).toLowerCase();
  return Object.values(SUBSCRIPTION_STATUS).includes(normalizedStatus)
    ? normalizedStatus
    : SUBSCRIPTION_STATUS.NONE;
}

export function normalizeExpiryDate(rawDate) {
  if (!rawDate) return null;

  const parsedDate = new Date(rawDate);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

export function getEntitlementList(user, tokenClaims = null) {
  const profileEntitlements = user?.profile?.entitlements;
  const topLevelEntitlements = user?.entitlements;
  const tokenEntitlements = tokenClaims?.entitlements;
  const rawEntitlements = Array.isArray(profileEntitlements)
    ? profileEntitlements
    : Array.isArray(topLevelEntitlements)
      ? topLevelEntitlements
      : Array.isArray(tokenEntitlements)
        ? tokenEntitlements
      : null;

  if (rawEntitlements) {
    return rawEntitlements.filter(Boolean);
  }

  // Tier-derived fallback: prefer profile (subscription-status source of truth)
  // over JWT claims so stale tokens don't over-inflate access.
  const normalizedTier = normalizeTier(user?.profile?.subscription_tier ?? tokenClaims?.subscription_tier);
  return TIER_ENTITLEMENTS[normalizedTier] || [];
}

export function buildSubscriptionSnapshot(user, tokenClaims = null) {
  // user.profile is the authoritative source: it is populated by the
  // GET /api/user/subscription-status/ call on every app init and visibility
  // change.  JWT claims (tokenClaims) serve as an immediate fallback right
  // after login / token refresh, before the status endpoint has responded.
  const status = normalizeStatus(user?.profile?.subscription_status ?? tokenClaims?.subscription_status);
  const tier = normalizeTier(user?.profile?.subscription_tier ?? tokenClaims?.subscription_tier);
  const expiryDate = normalizeExpiryDate(
    user?.profile?.subscription_expiry_date ?? tokenClaims?.subscription_expiry_date
  );
  const now = new Date();
  const isExpired = Boolean(expiryDate && expiryDate.getTime() < now.getTime());
  const isActiveStatus = [SUBSCRIPTION_STATUS.ACTIVE, SUBSCRIPTION_STATUS.TRIALING].includes(status);
  // subscription_status is the authoritative Stripe field.  When it is "active"
  // or "trialing", the subscription has been paid for and is genuinely active.
  // subscription_expiry_date maps to Stripe's current_period_end — the date of
  // the *next* billing cycle, not an access cutoff.  If a renewal webhook is
  // missed the expiry date goes stale while status stays "active", so gating on
  // isExpired here would incorrectly block a paying customer.
  // isExpired is kept in the snapshot for UI display purposes only.
  const isActive = isActiveStatus;
  const entitlements = getEntitlementList(user, tokenClaims);

  return {
    status,
    tier,
    expiryDate,
    isExpired,
    isActive,
    entitlements,
    entitlementSet: new Set(isActive ? entitlements : []),
  };
}

export function getFeaturePolicy(featureKey) {
  return FEATURE_ACCESS_POLICIES[featureKey] || null;
}

export function canAccessFeature(subscription, featureKey) {
  return subscription.entitlementSet.has(featureKey);
}

export function getFeatureAccessState(subscription, featureKey) {
  return {
    allowed: canAccessFeature(subscription, featureKey),
    featureKey,
    subscriptionStatus: subscription.status,
    subscriptionTier: subscription.tier,
    expiryDate: subscription.expiryDate,
    policy: getFeaturePolicy(featureKey),
  };
}